import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
  PapiDaUserProfileTemplatesFetch,
  PapiDaUserProfileTemplatesList,
} from '@wix/da-papi-types';
import { isGroup } from '@wix/da-shared-react/pkg/User/helpers';
import { withOffset } from '@wix/da-shared-react/pkg/Stream';
import { OffsetStream } from '@wix/da-shared-react/pkg/Stream/withOffset/types';
import { getStream } from '@wix/da-shared-react/pkg/Stream/base/selectors';
import { isApiError, request } from '@wix/da-shared-react/pkg/xhr';
import {
  fetchMoreCustomboxTemplates,
  saveCustomboxWithTemplate,
} from '../actions/customSectionTemplates';
import { API_BASE_PATH } from '../../constants';
import { streamId } from '../../types/customBoxTemplates';
import { getProfileOwnerUser } from '../selectors/users';
import { saveModuleConfig } from '../actions/modules';
import { putErrorNotification } from '@wix/da-shared-react/pkg/utils/saga';
import i18next from 'i18next';

function* handleFetchMoreTemplates() {
  const stream: OffsetStream = yield select(getStream, streamId);
  const profileOwnerUser = yield select(getProfileOwnerUser);
  const gruserType =
    profileOwnerUser && isGroup(profileOwnerUser) ? 'group' : 'profile';
  const response: PapiDaUserProfileTemplatesList = yield yield call(request, {
    method: 'get',
    url: '/templates/list',
    baseURL: API_BASE_PATH,
    params: {
      offset: stream.nextOffset,
      limit: stream.itemsPerFetch,
      type: gruserType,
    },
  });

  if (isApiError(response)) {
    yield put(
      withOffset.actions.fetchFailure({
        streamId,
        errorMsg: response.error,
      })
    );
  } else {
    yield put(
      withOffset.actions.fetchSuccess({
        streamId,
        items: response.results,
        hasMore: !!response.hasMore,
        nextOffset: response.nextOffset,
      })
    );
  }
}

function* handleSaveCustomboxWithTemplate(
  action: ReturnType<typeof saveCustomboxWithTemplate>
) {
  const fetchResponse: PapiDaUserProfileTemplatesFetch = yield call(request, {
    url: '/templates/fetch',
    baseURL: API_BASE_PATH,
    params: { templateid: action.payload.template.templateId },
  });

  if (isApiError(fetchResponse) || !fetchResponse.template) {
    yield putErrorNotification(
      i18next.t('widgets.custombox.templatePicker.fetchFailed')
    );
    return;
  }

  const fullTemplate = fetchResponse.template;
  yield put(
    saveModuleConfig({
      moduleId: action.payload.moduleId,
      title: fullTemplate.title,
      editorRaw: fullTemplate.customtext?.html.markup,
      backgroundType: fullTemplate.backgroundType,
      color: fullTemplate.color,
      gradient: fullTemplate.gradient,
      imageDisplayMode: fullTemplate.imageDisplayMode,
      imageTint: fullTemplate.imageTint,
      imageUrl: fullTemplate.backgroundImage,
      // pattern is stored as string URL, and we need to send its id to the server
      pattern: 0,
      // this only happens on module creation, the version should always be 0
      version: 0,
    })
  );
}

export default function* customBoxTemplatesSaga() {
  yield all([
    takeLatest(fetchMoreCustomboxTemplates, handleFetchMoreTemplates),
    takeLatest(saveCustomboxWithTemplate, handleSaveCustomboxWithTemplate),
  ]);
}
